import {
  ICONE_DA_VISIBILIDADE,
  ICONE_DO_MODO,
  ROTULO_DA_VISIBILIDADE,
  ROTULO_DO_MODO,
  type CompartilhamentoDaVisao,
  type ConfigDaVisao,
  type ModoDaVisao,
  type PapelNaVisao,
  type Visao,
  type VisibilidadeDaVisao,
} from '@orbind/core'
import { Button, FieldBlock, Input, Modal, Select, useToast } from '@orbind/ui'
import { useMemo, useState, type ReactNode } from 'react'
import { ColunasDoFiltro } from '../work-item/ColunasDoFiltro.tsx'
import type { DefinicaoDeColuna } from '../work-item/colunas-da-tabela.ts'
import { useMutacoesDeVisao } from './api.ts'
import './visao.css'

/**
 * Criar ou editar uma visão (SPEC-20261103-1418): nome, tipo de visualização,
 * filtro, colunas e ordenação da lista, e quem vê.
 *
 * O filtro é desenhado por quem chama (`renderFiltro`): o quadro usa o
 * construtor de campo/operador/valor, a lista de Itens usa o do filtro rápido.
 * Aqui ele é só um valor opaco que vai para `config.filtro`.
 *
 * Quem pode editar mas não é o dono vê "Quem vê" sem poder mudar: só o dono
 * decide com quem a visão é compartilhada.
 */

export type ColunasDaVisao = {
  catalogo: readonly DefinicaoDeColuna[]
  /** As colunas ligadas agora, sem o Título — ponto de partida das colunas próprias. */
  atuais: readonly string[]
}

type Filtro = Record<string, unknown> | null

const VISIBILIDADES: VisibilidadeDaVisao[] = ['private', 'shared', 'public']

const descricaoDaVisibilidade = (v: VisibilidadeDaVisao, ondeVale: string) =>
  v === 'private' ? 'Só você vê' : v === 'shared' ? 'Só as pessoas que você escolher' : `Todos que veem ${ondeVale}`

const PAPEIS: { value: PapelNaVisao; label: string }[] = [
  { value: 'viewer', label: 'Pode usar' },
  { value: 'editor', label: 'Pode editar' },
]

export function EditorDeVisao({
  quadroId,
  visao,
  modos,
  modoAtual,
  ondeVale,
  membros,
  meuId,
  filtroInicial,
  renderFiltro,
  colunas,
  ordenacaoAtual,
  onClose,
  onSalva,
  onApagada,
}: {
  quadroId: string | null
  /** null = nova visão. */
  visao: Visao | null
  modos: readonly ModoDaVisao[]
  /** O tipo em uso na tela: o ponto de partida de uma visão nova. */
  modoAtual: ModoDaVisao
  /** "este quadro" / "a lista de Itens de Trabalho" — para explicar a pública. */
  ondeVale: string
  membros: readonly { value: string; label: string }[]
  meuId: string
  /** O filtro de partida de uma visão nova (o que está aplicado na tela). */
  filtroInicial?: Filtro
  renderFiltro: (filtro: Filtro, mudar: (f: Filtro) => void) => ReactNode
  colunas?: ColunasDaVisao
  ordenacaoAtual?: ConfigDaVisao['ordenacao']
  onClose: () => void
  onSalva: (v: Visao) => void
  onApagada?: (id: string) => void
}) {
  const toast = useToast()
  const { criar, alterar, apagar } = useMutacoesDeVisao(quadroId)
  const dono = !visao || visao.mine

  const inicial = useMemo(
    () => ({
      name: visao?.name ?? '',
      modo: visao ? (visao.config.modo ?? modoAtual) : modoAtual,
      filtro: (visao ? (visao.config.filtro ?? null) : (filtroInicial ?? null)) as Filtro,
      colunas: visao ? visao.config.colunas : undefined,
      ordenacao: visao ? (visao.config.ordenacao ?? null) : (ordenacaoAtual ?? null),
      visibility: visao?.visibility ?? ('private' as VisibilidadeDaVisao),
      publicRole: visao?.publicRole ?? ('viewer' as PapelNaVisao),
      shares: visao?.shares ?? ([] as CompartilhamentoDaVisao[]),
    }),
    // o rascunho nasce uma vez por abertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visao?.id],
  )
  const [rascunho, setRascunho] = useState(inicial)
  const mudar = <K extends keyof typeof rascunho>(k: K, v: (typeof rascunho)[K]) =>
    setRascunho((r) => ({ ...r, [k]: v }))

  const sujo = JSON.stringify(rascunho) !== JSON.stringify(inicial)
  const semPessoas = rascunho.visibility === 'shared' && rascunho.shares.length === 0
  const salvando = criar.isPending || alterar.isPending
  const podeSalvar = rascunho.name.trim() !== '' && !semPessoas && !salvando && (visao === null || sujo)

  const salvar = async () => {
    if (!podeSalvar) return
    const config: ConfigDaVisao = {
      modo: rascunho.modo,
      filtro: rascunho.filtro,
      ...(rascunho.colunas ? { colunas: rascunho.colunas } : {}),
      ordenacao: rascunho.ordenacao,
    }
    const quemVe = dono
      ? {
          visibility: rascunho.visibility,
          publicRole: rascunho.publicRole,
          shares: rascunho.visibility === 'shared' ? rascunho.shares : [],
        }
      : {}
    try {
      const salva = visao
        ? await alterar.mutateAsync({
            id: visao.id,
            name: rascunho.name.trim(),
            // colunas desligadas ("as de quem abre") precisam sair da visão
            config: { ...config, colunas: rascunho.colunas },
            ...quemVe,
          })
        : await criar.mutateAsync({
            name: rascunho.name.trim(),
            visibility: rascunho.visibility,
            publicRole: rascunho.publicRole,
            shares: rascunho.visibility === 'shared' ? rascunho.shares : [],
            config,
          })
      toast.push({ tone: 'success', message: visao ? 'Visão salva.' : 'Visão criada.' })
      onSalva(salva)
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' })
    }
  }

  const remover = async () => {
    if (!visao) return
    try {
      await apagar.mutateAsync(visao.id)
      toast.push({ tone: 'success', message: 'Visão apagada.' })
      onApagada?.(visao.id)
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível apagar.' })
    }
  }

  const nomeDe = (id: string) => membros.find((m) => m.value === id)?.label ?? 'Pessoa'
  const paraAdicionar = membros.filter((m) => m.value !== meuId && !rascunho.shares.some((s) => s.memberId === m.value))
  const colunasDaOrdem = (colunas?.catalogo ?? []).map((c) => ({ value: c.key, label: c.rotulo }))

  return (
    <Modal
      open
      onClose={onClose}
      title={visao ? 'Editar visão' : 'Nova visão'}
      width={820}
      footer={
        <>
          {visao?.mine ? (
            <Button variant="danger" onClick={() => void remover()} disabled={apagar.isPending}>
              Apagar
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!podeSalvar} onClick={() => void salvar()}>
            {salvando ? 'Salvando…' : visao ? 'Salvar' : 'Criar e aplicar'}
          </Button>
        </>
      }
    >
      <div className="ob-stack vis-ed">
        <FieldBlock label="Nome" required>
          {({ id }) => (
            <Input
              id={id}
              autoFocus
              placeholder="Ex.: Atrasados do time em lista"
              value={rascunho.name}
              onChange={(e) => mudar('name', e.target.value)}
            />
          )}
        </FieldBlock>

        <section className="vis-ed__secao" aria-labelledby="vis-ed-modo">
          <span id="vis-ed-modo" className="vis-ed__titulo">
            Tipo de visualização
          </span>
          <div className="vis-ed__opcoes" role="radiogroup" aria-labelledby="vis-ed-modo">
            {modos.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={rascunho.modo === m}
                className={`vis-ed__opcao${rascunho.modo === m ? ' vis-ed__opcao--on' : ''}`}
                onClick={() => mudar('modo', m)}
              >
                <i className={ICONE_DO_MODO[m]} aria-hidden />
                {ROTULO_DO_MODO[m]}
              </button>
            ))}
          </div>
        </section>

        <section className="vis-ed__secao" aria-labelledby="vis-ed-filtro">
          <span id="vis-ed-filtro" className="vis-ed__titulo">
            Filtro
          </span>
          {renderFiltro(rascunho.filtro, (f) => mudar('filtro', f))}
        </section>

        {colunas ? (
          <>
            <ColunasDoFiltro
              catalogo={colunas.catalogo}
              valor={rascunho.colunas}
              atuais={colunas.atuais}
              onChange={(c) => mudar('colunas', c)}
            />
            <section className="vis-ed__secao" aria-labelledby="vis-ed-ordem">
              <span id="vis-ed-ordem" className="vis-ed__titulo">
                Ordenação da lista
              </span>
              <div className="vis-ed__linha">
                <Select
                  aria-labelledby="vis-ed-ordem"
                  value={rascunho.ordenacao?.k ?? ''}
                  placeholder="Sem ordenação"
                  options={[{ value: '', label: 'Sem ordenação' }, ...colunasDaOrdem]}
                  onChange={(k) => mudar('ordenacao', k ? { k, dir: rascunho.ordenacao?.dir ?? 'asc' } : null)}
                />
                <Select
                  aria-labelledby="vis-ed-ordem"
                  disabled={!rascunho.ordenacao}
                  value={rascunho.ordenacao?.dir ?? 'asc'}
                  options={[
                    { value: 'asc', label: 'Crescente (A→Z, 1→9)' },
                    { value: 'desc', label: 'Decrescente (Z→A, 9→1)' },
                  ]}
                  onChange={(dir) =>
                    rascunho.ordenacao && mudar('ordenacao', { k: rascunho.ordenacao.k, dir: dir as 'asc' | 'desc' })
                  }
                />
              </div>
            </section>
          </>
        ) : null}

        <section className="vis-ed__secao" aria-labelledby="vis-ed-quem">
          <span id="vis-ed-quem" className="vis-ed__titulo">
            Quem vê
          </span>
          {!dono ? (
            <p className="vis-ed__dica">
              <i className={ICONE_DA_VISIBILIDADE[rascunho.visibility]} aria-hidden />{' '}
              {ROTULO_DA_VISIBILIDADE[rascunho.visibility]} — só {visao?.owner.name ?? 'o dono'}, dono da visão, muda
              quem a vê.
            </p>
          ) : (
            <>
              <div className="vis-ed__opcoes" role="radiogroup" aria-labelledby="vis-ed-quem">
                {VISIBILIDADES.map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={rascunho.visibility === v}
                    aria-label={`${ROTULO_DA_VISIBILIDADE[v]}: ${descricaoDaVisibilidade(v, ondeVale)}`}
                    className={`vis-ed__opcao vis-ed__opcao--alta${rascunho.visibility === v ? ' vis-ed__opcao--on' : ''}`}
                    onClick={() => mudar('visibility', v)}
                  >
                    <i className={ICONE_DA_VISIBILIDADE[v]} aria-hidden />
                    <span>
                      {ROTULO_DA_VISIBILIDADE[v]}
                      <small>{descricaoDaVisibilidade(v, ondeVale)}</small>
                    </span>
                  </button>
                ))}
              </div>

              {rascunho.visibility === 'public' ? (
                <div className="vis-ed__linha">
                  <span className="vis-ed__rotulo">Todos</span>
                  <Select
                    value={rascunho.publicRole}
                    options={PAPEIS}
                    onChange={(p) => mudar('publicRole', p)}
                  />
                </div>
              ) : null}

              {rascunho.visibility === 'shared' ? (
                <div className="vis-ed__pessoas">
                  {rascunho.shares.length === 0 ? (
                    <p className="vis-ed__dica">Escolha ao menos uma pessoa.</p>
                  ) : (
                    <ul className="vis-ed__lista">
                      {rascunho.shares.map((s) => (
                        <li key={s.memberId} className="vis-ed__pessoa">
                          <i className="fas fa-user" aria-hidden />
                          <span className="vis-ed__nome">{nomeDe(s.memberId)}</span>
                          <Select
                            value={s.role}
                            options={PAPEIS}
                            onChange={(role) =>
                              mudar(
                                'shares',
                                rascunho.shares.map((x) => (x.memberId === s.memberId ? { ...x, role } : x)),
                              )
                            }
                          />
                          <button
                            type="button"
                            className="vis-ed__tirar"
                            aria-label={`Tirar ${nomeDe(s.memberId)}`}
                            title="Tirar"
                            onClick={() =>
                              mudar(
                                'shares',
                                rascunho.shares.filter((x) => x.memberId !== s.memberId),
                              )
                            }
                          >
                            <i className="fas fa-times" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {paraAdicionar.length > 0 ? (
                    <Select
                      value={null}
                      placeholder="+ Adicionar pessoa"
                      options={paraAdicionar}
                      onChange={(memberId) =>
                        mudar('shares', [...rascunho.shares, { memberId, role: 'viewer' }])
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </Modal>
  )
}
